import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useSelector } from '@legendapp/state/react';
import { auth$ } from '@/stores/auth$';
import { ui$, setActiveNoteId } from '@/stores/ui$';
import { notes$, updateNote, deleteNote, addTag, removeTag, protectNote, unprotectNote, isSmartTag, setSmartTags, SMART_TAG_PREFIX } from '@/stores/notes$';
import { snippets$, createSnippet, deleteSnippet, bulkDeleteSnippets } from '@/stores/snippets$';
import { pin$, isNoteUnlocked, lockNote, protectNoteContent, decryptNoteContent } from '@/stores/pin$';
import SnippetItem from './SnippetItem';
import SnippetFilterBar from './SnippetFilterBar';
import ImagePreview from './ImagePreview';
import { useImageUpload } from '@/hooks/useImageUpload';
import { Send, Tag, MoreVertical, Trash2, X, ArrowLeft, ImageIcon, Loader2, Share2, Eye, Lock, LockOpen, Shield, ShieldOff, Sparkles, Users, RefreshCw, FileText, Flag, Puzzle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAIAction, classifyNote } from '@/hooks/useAIAction';
import { isTagShared, sharedTagPaths$ } from '@/stores/sharedTags$';
import { trackEvent, setOnceUserProperties } from '@/lib/analytics';
import { getExistingShare } from '@/stores/sharedNotes$';

// Lazy load modals — only loaded when user triggers them
const ShareNoteModal = lazy(() => import('./ShareNoteModal'));
const PinSetupModal = lazy(() => import('./PinSetupModal'));
const PinEntryModal = lazy(() => import('./PinEntryModal'));
const ProtectNoteModal = lazy(() => import('./ProtectNoteModal'));
const PinResetModal = lazy(() => import('./PinResetModal'));
const ReportContentModal = lazy(() => import('./ReportContentModal'));
import { format } from 'date-fns';
import TagAutocomplete from './TagAutocomplete';
import TagEditAutocomplete from './TagEditAutocomplete';
import { v4 as uuidv4 } from 'uuid';

export default function MainEditor() {
  const user = useSelector(() => auth$.user.get());
  const activeNoteId = useSelector(() => ui$.activeNoteId.get());
  const snippetFilter = useSelector(() => ui$.snippetFilter.get());
  const selectedSnippetIds = useSelector(() => ui$.selectedSnippetIds.get());
  const viewingSharedNote = useSelector(() => ui$.viewingSharedNote.get());
  const smartTagsEnabled = useSelector(() => ui$.smartTagsEnabled.get());
  const isReadOnly = !!viewingSharedNote && viewingSharedNote.noteId === activeNoteId;

  // Shared tags reactivity (for blue shared tag styling)
  useSelector(() => sharedTagPaths$.get());

  // PIN protection state
  const hasPinSetup = useSelector(() => pin$.hasPinSetup.get());
  const unlockedNoteIds = useSelector(() => pin$.unlockedNoteIds.get());

  // Get active note with snippets (or shared note data for read-only view)
  const activeNote = useSelector(() => {
    if (!activeNoteId) return null;

    // If viewing a shared note from another user, use the shared note data
    if (isReadOnly && viewingSharedNote?.note) {
      const sn = viewingSharedNote.note;
      return {
        id: sn.id,
        title: sn.title,
        tags: sn.tags || [],
        created_at: sn.created_at,
        updated_at: sn.updated_at,
        snippets: (sn.snippets || []).sort((a, b) => (a.version || 0) - (b.version || 0)),
      };
    }

    const note = notes$[activeNoteId].get();
    if (!note || note.deleted || note.deleted_at) return null;

    const snippetsObj = snippets$.get();
    const noteSnippets = Object.values(snippetsObj)
      .filter(s => s && s.note_id === activeNoteId && !s.deleted)
      .sort((a, b) => a.version - b.version);

    return {
      ...note,
      snippets: noteSnippets,
    };
  });

  // Protection state for current note
  const isProtected = activeNote?.is_protected === true;
  const isUnlocked = isProtected && unlockedNoteIds.includes(activeNoteId);
  const isLocked = isProtected && !isUnlocked;

  // Filter snippets based on active filter
  const filteredSnippets = activeNote?.snippets.filter(snippet => {
    if (!snippetFilter) return true;
    if (snippetFilter === 'upvoted') return snippet.upvotes > 0;
    if (snippetFilter === 'favorited') return snippet.favorite;
    return true;
  }) || [];

  const [inputContent, setInputContent] = useState('');
  const [inputTitle, setInputTitle] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [noteTitleInput, setNoteTitleInput] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  // PIN protection modal states
  const [isPinSetupModalOpen, setIsPinSetupModalOpen] = useState(false);
  const [isPinEntryModalOpen, setIsPinEntryModalOpen] = useState(false);
  const [isProtectModalOpen, setIsProtectModalOpen] = useState(false);
  const [isPinResetModalOpen, setIsPinResetModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
  const [noteShareData, setNoteShareData] = useState(null);
  const { suggestTitle, loading: aiLoading, error: aiError, result: titleSuggestions, clearResult: clearTitleSuggestions, abort: abortAI } = useAIAction();
  const feedEndRef = useRef(null);
  const titleInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const classifyTimerRef = useRef(null);

  // Image upload hook
  const {
    pendingImages,
    isUploading,
    error: imageError,
    addImages,
    removeImage,
    clearImages,
    uploadAll,
    canAddMore,
  } = useImageUpload();
  
  // Update local title state when active note changes
  useEffect(() => {
    if (activeNote) {
      setNoteTitleInput(activeNote.title);
    }
    // Close suggestions and abort in-flight AI request when switching notes
    setShowTitleSuggestions(false);
    clearTitleSuggestions();
    abortAI();
    // Cancel pending classify
    if (classifyTimerRef.current) clearTimeout(classifyTimerRef.current);
  }, [activeNote?.id]);

  // Auto-focus title on new note creation
  useEffect(() => {
    if (activeNote && !activeNote.title.trim() && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [activeNote?.id]);

  const scrollToBottom = () => {
    feedEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeNote?.snippets.length]);

  const handleTitleChange = (e) => {
    setNoteTitleInput(e.target.value);
  };

  const handleTitleBlur = () => {
    if (!activeNote) return;

    const trimmedTitle = noteTitleInput.trim();

    // If empty, generate timestamp-based title
    if (!trimmedTitle) {
      const timestampTitle = `Note - ${format(new Date(activeNote.created_at), 'MMM d, h:mm a')}`;
      setNoteTitleInput(timestampTitle);
      updateNote(activeNote.id, { title: timestampTitle });
    } else if (trimmedTitle !== activeNote.title) {
      // Only update if changed
      updateNote(activeNote.id, { title: trimmedTitle });
    }
  };

  const handleSuggestTitle = async () => {
    if (!activeNote || aiLoading) return;
    clearTitleSuggestions();
    setShowTitleSuggestions(true);
    trackEvent('ai_title_suggested', { note_id: activeNote.id });
    setOnceUserProperties({ first_ai_used_at: new Date().toISOString() });
    await suggestTitle(activeNote.id);
  };

  const handleApplyTitle = (title) => {
    if (!activeNote) return;
    setNoteTitleInput(title);
    updateNote(activeNote.id, { title });
    setShowTitleSuggestions(false);
    clearTitleSuggestions();
    trackEvent('ai_title_applied', { note_id: activeNote.id });
  };

  const handleAddTag = (tag) => {
    if (!activeNote) return;
    addTag(activeNote.id, tag);
  };

  const handleRemoveTag = (tag) => {
    if (!activeNote) return;
    removeTag(activeNote.id, tag);
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
  };

  const handleSaveTagEdit = (oldTag, newTag) => {
    const trimmedTag = newTag.trim();

    // Validation: prevent empty tags
    if (!trimmedTag) {
      setEditingTag(null);
      return;
    }

    // If unchanged, just close
    if (trimmedTag === oldTag) {
      setEditingTag(null);
      return;
    }

    // Remove old tag, add new tag (duplicate tags are handled by addTag - it won't add if exists)
    removeTag(activeNote.id, oldTag);
    addTag(activeNote.id, trimmedTag);
    setEditingTag(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    // Allow submit if content OR images exist
    const hasContent = inputContent.trim();
    const hasImages = pendingImages.length > 0;
    if ((!hasContent && !hasImages) || !activeNoteId || !user) return;

    setIsSubmitting(true);
    try {
      let imageUrls = [];

      // Upload images if any
      if (hasImages) {
        const snippetId = uuidv4();
        imageUrls = await uploadAll(user.id, snippetId);

        // Create snippet with uploaded image URLs
        createSnippet(activeNoteId, inputContent, inputTitle, user.id, (noteId) => {
          updateNote(noteId, { updated_at: new Date().toISOString() });
        }, imageUrls, snippetId);
      } else {
        // No images, create snippet normally
        createSnippet(activeNoteId, inputContent, inputTitle, user.id, (noteId) => {
          updateNote(noteId, { updated_at: new Date().toISOString() });
        });
      }

      setInputContent('');
      setInputTitle('');
      clearImages();

      // Auto-classify after snippet save (debounced) — skip if disabled
      if (ui$.smartTagsEnabled.get()) {
        if (classifyTimerRef.current) clearTimeout(classifyTimerRef.current);
        const noteIdToClassify = activeNoteId;
        classifyTimerRef.current = setTimeout(() => {
          classifyNote(noteIdToClassify, (smartTags) => {
            setSmartTags(noteIdToClassify, smartTags);
            trackEvent('smart_tags_classified', { tag_count: smartTags.length });
          });
        }, 3000);
      }
    } catch (err) {
      console.error('[MainEditor] Failed to submit:', err);
      // Error is already set in useImageUpload hook
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle paste for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = Array.from(items)
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter(Boolean);

    if (imageFiles.length > 0) {
      e.preventDefault();
      addImages(imageFiles);
    }
  };

  // Handle file input change
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addImages(e.target.files);
      e.target.value = ''; // Reset to allow same file selection
    }
  };
  
  const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          handleSubmit();
      }
  }

  // Handle protect note action
  const handleProtectNote = () => {
    setIsMenuOpen(false);
    if (!hasPinSetup) {
      // First time - show PIN setup modal
      setIsPinSetupModalOpen(true);
    } else {
      // PIN already set up - show confirmation modal
      setIsProtectModalOpen(true);
    }
  };

  // Handle PIN setup success - then protect the note
  const handlePinSetupSuccess = async () => {
    setIsPinSetupModalOpen(false);
    // After PIN setup, show protect confirmation
    setIsProtectModalOpen(true);
  };

  // Handle protect confirmation - encrypt and save
  const handleProtectConfirm = async (pin) => {
    try {
      // Encrypt all snippets for this note
      const snippets = activeNote.snippets || [];
      const encryptedContent = await protectNoteContent(activeNoteId, snippets, pin);

      // Update note with protection
      protectNote(activeNoteId, encryptedContent);

      setIsProtectModalOpen(false);
      console.log('[MainEditor] Note protected successfully');
    } catch (err) {
      console.error('[MainEditor] Failed to protect note:', err);
    }
  };

  // Handle unlock note
  const handleUnlockNote = () => {
    setIsPinEntryModalOpen(true);
  };

  // Handle unlock success
  const handleUnlockSuccess = () => {
    setIsPinEntryModalOpen(false);
    console.log('[MainEditor] Note unlocked');
  };

  // Handle quick lock
  const handleLockNote = () => {
    lockNote(activeNoteId);
    setIsMenuOpen(false);
  };

  // Handle remove protection
  const handleRemoveProtection = async () => {
    setIsMenuOpen(false);
    // Need to verify PIN first
    setIsPinEntryModalOpen(true);
    // After successful unlock, remove protection
    // This is handled by checking state after modal closes
  };

  // Handle forgot PIN
  const handleForgotPin = () => {
    setIsPinEntryModalOpen(false);
    setIsPinResetModalOpen(true);
  };

  // Handle PIN reset success
  const handlePinResetSuccess = () => {
    setIsPinResetModalOpen(false);
    // Note will be deleted by reset, so clear active note
    setActiveNoteId(null);
  };

  if (!activeNote) {
    return (
        // Hidden on mobile (< md), shown on desktop
        <div className="hidden md:flex flex-1 flex-col items-center justify-center p-8 text-center bg-background/50">
            <div className="max-w-md space-y-3">
                <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <FileText size={24} className="text-muted-foreground/50" />
                </div>
                <h3 className="text-lg font-medium">Select a note to view</h3>
                <p className="text-sm text-muted-foreground">Choose a note from the list, or press the <kbd className="px-1.5 py-0.5 text-[11px] font-mono bg-muted border border-border rounded">+</kbd> button to create one.</p>
            </div>
        </div>
    );
  }

  return (
    <div className={cn(
      "flex-1 flex flex-col h-full min-h-0 bg-background min-w-0 pr-[var(--sar)]",
      // Mobile: hide when no active note (show list instead)
      "md:flex",
      activeNoteId ? "flex" : "hidden md:flex",
      // Mobile: top padding handled by App.jsx wrapper
    )}>
        {/* Read-only banner */}
        {isReadOnly && (
          <div className="px-6 py-2 bg-muted/50 border-b border-border text-xs text-muted-foreground flex items-center gap-2 z-10">
            <Eye size={12} />
            <span className="flex-1">View only — owned by <span className="font-medium text-foreground">{viewingSharedNote.authorName}</span></span>
            <button
              onClick={() => setIsReportModalOpen(true)}
              className="flex items-center gap-1 px-2 py-0.5 rounded text-destructive/70 hover:text-destructive hover:bg-destructive/10 transition-colors"
              title="Report content"
            >
              <Flag size={11} />
              <span>Report</span>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur z-editor-header">
             {/* Mobile back button */}
             <button
               onClick={() => {
                 setActiveNoteId(null);
                 if (isReadOnly) ui$.viewingSharedNote.set(null);
               }}
               className="md:hidden p-2 -ml-2 mr-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors"
               aria-label="Back to notes"
             >
               <ArrowLeft size={20} />
             </button>

             <div className="min-w-0 flex-1">
                 {isReadOnly ? (
                   <h1 className="text-xl font-bold truncate">{activeNote.title || 'Untitled Note'}</h1>
                 ) : (
                   <input
                      ref={titleInputRef}
                      type="text"
                      value={noteTitleInput}
                      onChange={handleTitleChange}
                      onBlur={handleTitleBlur}
                      className="w-full bg-transparent text-xl font-bold border-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 truncate"
                      placeholder="Untitled Note"
                   />
                 )}
                 <div className="flex items-center gap-2 mt-1 flex-wrap">
                     {/* User tags (editable) — shared tags get blue styling */}
                     {activeNote.tags.filter(t => !isSmartTag(t)).map(tag => {
                        const shared = isTagShared(tag);
                        return (
                        <div key={tag} className="inline-flex">
                          {!isReadOnly && editingTag === tag ? (
                            <TagEditAutocomplete
                              initialValue={tag}
                              onSave={(newTag) => handleSaveTagEdit(tag, newTag)}
                              onCancel={() => setEditingTag(null)}
                            />
                          ) : (
                            <span
                              onClick={() => !isReadOnly && handleEditTag(tag)}
                              className={cn(
                                "inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium max-w-[200px]",
                                shared
                                  ? "bg-blue-500/15 text-blue-700 dark:text-blue-400"
                                  : "bg-muted text-muted-foreground",
                                !isReadOnly && "group transition-colors cursor-pointer",
                                !isReadOnly && (shared ? "hover:bg-blue-500/25" : "hover:bg-muted/80")
                              )}
                              title={shared ? `Shared tag: #${tag}` : tag.length > 30 ? `#${tag}` : isReadOnly ? `#${tag}` : 'Click to edit'}
                            >
                              {shared && <Users size={10} className="flex-shrink-0" />}
                              <span className="truncate">#{tag}</span>
                              {!isReadOnly && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveTag(tag);
                                  }}
                                  className={cn(
                                    "p-1 -mr-0.5 rounded opacity-0 group-hover:opacity-100 transition-all flex-shrink-0",
                                    shared ? "hover:bg-blue-500/20 hover:text-blue-900 dark:hover:text-blue-200" : "hover:bg-destructive/10 hover:text-destructive"
                                  )}
                                  title="Remove tag"
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                        );
                     })}

                     {/* Smart tags (read-only, amber) — hidden when disabled */}
                     {smartTagsEnabled && activeNote.tags.filter(t => isSmartTag(t)).map(tag => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 max-w-[200px]"
                          title={`Smart tag: ${tag.slice(SMART_TAG_PREFIX.length)}`}
                        >
                          <Sparkles size={10} className="flex-shrink-0" />
                          <span className="truncate">{tag.slice(SMART_TAG_PREFIX.length)}</span>
                        </span>
                     ))}

                     {!isReadOnly && (showTagInput ? (
                       <TagAutocomplete
                         noteId={activeNote.id}
                         onAddTag={(tag) => {
                           handleAddTag(tag);
                           setShowTagInput(false);
                         }}
                         onClose={() => setShowTagInput(false)}
                       />
                     ) : (
                       <button
                         onClick={() => setShowTagInput(true)}
                         className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1 transition-colors"
                       >
                          <Tag size={12} /> Add tags
                       </button>
                     ))}
                 </div>
             </div>

             {/* AI Suggest Title */}
             {!isReadOnly && !isLocked && (
               <div className="relative">
                  <button
                      onClick={handleSuggestTitle}
                      disabled={aiLoading || !activeNote?.snippets?.length}
                      className={cn(
                        "p-2 rounded-md transition-colors",
                        aiLoading
                          ? "text-primary animate-pulse"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                        (!activeNote?.snippets?.length) && "opacity-30 cursor-not-allowed"
                      )}
                      title={activeNote?.snippets?.length ? "Suggest title with AI" : "Add snippets first"}
                  >
                      {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  </button>

                  {/* Title suggestions popover */}
                  {showTitleSuggestions && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                      <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b border-border/50 mb-1 flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Sparkles size={12} />
                          Suggested Titles
                        </span>
                        <button
                          onClick={() => { setShowTitleSuggestions(false); clearTitleSuggestions(); }}
                          className="p-0.5 hover:bg-accent rounded"
                        >
                          <X size={12} />
                        </button>
                      </div>

                      {aiLoading && (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          <Loader2 size={16} className="animate-spin mx-auto mb-2" />
                          Generating...
                        </div>
                      )}

                      {aiError && (
                        <div className="px-3 py-2 text-xs text-destructive">
                          {aiError}
                        </div>
                      )}

                      {titleSuggestions && titleSuggestions.map((title, i) => (
                        <button
                          key={i}
                          onClick={() => handleApplyTitle(title)}
                          className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors truncate"
                          title={title}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                  )}
               </div>
             )}

             {!isReadOnly && (
               <div className="relative">
                  <button
                      onClick={() => {
                        const opening = !isMenuOpen;
                        setIsMenuOpen(opening);
                        if (opening && activeNoteId) {
                          getExistingShare(activeNoteId).then(setNoteShareData).catch(() => setNoteShareData(null));
                        }
                      }}
                      onBlur={() => setTimeout(() => setIsMenuOpen(false), 200)}
                      className="p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
                      data-testid="note-more-menu"
                  >
                      <MoreVertical size={18} />
                  </button>

                  {isMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-52 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                          <div className="px-3 py-2 text-xs font-semibold text-muted-foreground border-b border-border/50 mb-1">
                              Note Actions
                          </div>

                          {/* Share - disabled for protected notes */}
                          <button
                              className={cn(
                                "w-full text-left px-3 py-2 text-sm flex items-center gap-2",
                                isProtected
                                  ? "text-muted-foreground cursor-not-allowed"
                                  : "text-foreground hover:bg-accent"
                              )}
                              onClick={() => {
                                if (!isProtected) {
                                  setIsShareModalOpen(true);
                                  setIsMenuOpen(false);
                                }
                              }}
                              disabled={isProtected}
                              title={isProtected ? "Cannot share protected notes" : "Share this note"}
                          >
                              <Share2 size={14} />
                              <span>Share Note</span>
                              {isProtected && <Lock size={10} className="ml-auto opacity-50" />}
                          </button>

                          {/* Divider */}
                          <div className="my-1 border-t border-border/50" />

                          {/* Protection actions */}
                          {!isProtected ? (
                            // Not protected - show "Protect Note"
                            <button
                                className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                                onClick={handleProtectNote}
                            >
                                <Shield size={14} />
                                <span>Protect with PIN</span>
                            </button>
                          ) : isUnlocked ? (
                            // Protected and unlocked - show "Lock" and "Remove Protection"
                            <>
                              <button
                                  className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                                  onClick={handleLockNote}
                              >
                                  <Lock size={14} />
                                  <span>Lock Note</span>
                              </button>
                              <button
                                  className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                                  onClick={() => {
                                    // Remove protection
                                    unprotectNote(activeNoteId);
                                    setIsMenuOpen(false);
                                  }}
                              >
                                  <ShieldOff size={14} />
                                  <span>Remove Protection</span>
                              </button>
                            </>
                          ) : (
                            // Protected but locked - show "Unlock"
                            <button
                                className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30 flex items-center gap-2"
                                onClick={() => {
                                  handleUnlockNote();
                                  setIsMenuOpen(false);
                                }}
                            >
                                <LockOpen size={14} />
                                <span>Unlock Note</span>
                            </button>
                          )}

                          {/* Reclassify Smart Tags */}
                          {smartTagsEnabled && !isProtected && activeNote?.snippets?.length > 0 && (
                            <button
                              className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                              onClick={() => {
                                classifyNote(activeNoteId, (smartTags) => {
                                  setSmartTags(activeNoteId, smartTags);
                                  trackEvent('smart_tags_classified', { tag_count: smartTags.length, manual: true });
                                });
                                setIsMenuOpen(false);
                              }}
                            >
                              <RefreshCw size={14} />
                              <span>Reclassify Smart Tags</span>
                            </button>
                          )}

                          {/* Divider */}
                          <div className="my-1 border-t border-border/50" />

                          {/* Note Info */}
                          {(() => {
                            const snippets = activeNote?.snippets || [];
                            const snippetCount = snippets.length;
                            const wordCount = snippets.reduce((sum, s) => sum + (s.content?.split(/\s+/).filter(Boolean).length || 0), 0);
                            const totalCopies = snippets.reduce((sum, s) => sum + (s.copy_count || 0), 0);
                            const createdAt = activeNote?.created_at;
                            const viewCount = noteShareData?.view_count;
                            return (
                              <div className="px-3 py-2 text-[11px] text-muted-foreground space-y-0.5">
                                {createdAt && <div>Created {format(new Date(createdAt), 'MMM d, yyyy')}</div>}
                                <div>{snippetCount} snippet{snippetCount !== 1 ? 's' : ''} · {wordCount.toLocaleString()} word{wordCount !== 1 ? 's' : ''}</div>
                                {totalCopies > 0 && <div>{totalCopies} cop{totalCopies !== 1 ? 'ies' : 'y'}</div>}
                                {viewCount > 0 && <div>{viewCount} share view{viewCount !== 1 ? 's' : ''}</div>}
                              </div>
                            );
                          })()}

                          {/* Divider */}
                          <div className="my-1 border-t border-border/50" />

                          {/* Change Type: Move to Skills / Move to Notes */}
                          {activeNote?.note_type === 'skill' ? (
                            <button
                              className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                              onClick={() => {
                                updateNote(activeNoteId, { note_type: 'note' });
                                setIsMenuOpen(false);
                              }}
                            >
                              <FileText size={14} />
                              <span>Move to Notes</span>
                            </button>
                          ) : (
                            <button
                              className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-accent flex items-center gap-2"
                              onClick={() => {
                                updateNote(activeNoteId, { note_type: 'skill' });
                                setIsMenuOpen(false);
                              }}
                            >
                              <Puzzle size={14} />
                              <span>Move to Skills</span>
                            </button>
                          )}

                          {/* Divider */}
                          <div className="my-1 border-t border-border/50" />

                          {/* Delete */}
                          <button
                              className="w-full text-left px-3 py-2 text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                              onClick={() => {
                                deleteNote(activeNoteId, () => setActiveNoteId(null));
                                setIsMenuOpen(false);
                              }}
                          >
                              <Trash2 size={14} />
                              <span>Delete Note</span>
                          </button>
                      </div>
                  )}
               </div>
             )}
        </div>

        {/* Snippet Filter Bar - hidden when locked */}
        {!isLocked && <SnippetFilterBar noteId={activeNote.id} />}

        {/* Feed Body */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6 scroll-smooth">
            <div className="max-w-3xl mx-auto space-y-6">
                {/* Locked state - show unlock prompt */}
                {isLocked ? (
                  <div className="py-20 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 mb-4">
                      <Lock size={32} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Protected Note</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
                      This note is protected with a PIN. Enter your PIN to view the content.
                    </p>
                    <button
                      onClick={handleUnlockNote}
                      className="px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      Enter PIN to Unlock
                    </button>
                  </div>
                ) : activeNote.snippets.length === 0 ? (
                     <div className="py-20 text-center text-muted-foreground">
                         <p className="text-sm">Empty - no active versions</p>
                         <p className="text-xs mt-2 opacity-60">Start typing below to add v1</p>
                     </div>
                ) : filteredSnippets.length === 0 ? (
                     <div className="py-20 text-center text-muted-foreground">
                         <p>No snippets match the current filter.</p>
                     </div>
                ) : (
                    filteredSnippets.map((snippet) => (
                        <SnippetItem key={snippet.id} snippet={snippet} noteId={activeNote.id} />
                    ))
                )}
                <div ref={feedEndRef} className="h-4" />
            </div>
        </div>

        {/* Input Footer - hidden in read-only mode and when locked */}
        {!isReadOnly && !isLocked && <div className="p-4 border-t border-border bg-background">
             <div className="max-w-3xl mx-auto bg-card border border-border shadow-sm rounded-xl overflow-hidden focus-within:ring-1 focus-within:ring-primary/20 focus-within:border-primary/50 transition-all">
                 <div className="flex items-center px-3 pt-2">
                     <input 
                        type="text" 
                        placeholder="Snippet Title (Optional)"
                        value={inputTitle}
                        onChange={e => setInputTitle(e.target.value)} 
                        className="flex-1 bg-transparent text-sm font-medium border-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground/60 h-8"
                     />
                 </div>
                 
                 <div className="px-3 pb-2">
                     <textarea
                        ref={textareaRef}
                        value={inputContent}
                        onChange={e => setInputContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder="Write your prompt or note here... (Cmd+Enter to send)"
                        className="w-full bg-transparent resize-none min-h-[80px] text-sm leading-relaxed border-none focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 font-sans"
                        data-testid="snippet-input"
                     />
                 </div>

                 {/* Image Preview Area */}
                 {pendingImages.length > 0 && (
                   <div className="px-3 pb-2 border-t border-border/30">
                     <ImagePreview
                       images={pendingImages}
                       onRemove={removeImage}
                       editable={true}
                       canAddMore={canAddMore}
                       onAdd={addImages}
                       size="md"
                     />
                     {imageError && (
                       <p className="text-xs text-destructive mt-1">{imageError}</p>
                     )}
                   </div>
                 )}

                 {/* Hidden file input */}
                 <input
                   ref={fileInputRef}
                   type="file"
                   accept="image/jpeg,image/png,image/gif,image/webp"
                   multiple
                   onChange={handleFileSelect}
                   className="hidden"
                 />

                 <div className="px-3 pb-2 flex items-center justify-between border-t border-border/40 pt-2 bg-muted/10">
                     <div className="flex items-center text-xs text-muted-foreground gap-3">
                         {/* Image upload button */}
                         <button
                           type="button"
                           onClick={() => fileInputRef.current?.click()}
                           disabled={!canAddMore || isSubmitting}
                           className={cn(
                             "p-1.5 rounded-md transition-colors",
                             canAddMore
                               ? "hover:bg-accent hover:text-accent-foreground"
                               : "opacity-50 cursor-not-allowed"
                           )}
                           title={canAddMore ? "Add images (max 10)" : "Maximum 10 images"}
                         >
                           <ImageIcon size={14} />
                         </button>
                         <span>Markdown supported</span>
                     </div>
                     <button
                         onClick={handleSubmit}
                         disabled={(!inputContent.trim() && pendingImages.length === 0) || isSubmitting}
                         className="flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground text-xs font-semibold rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                         data-testid="submit-snippet-button"
                     >
                         {isSubmitting ? (
                           <>
                             <Loader2 size={12} className="animate-spin" />
                             <span>Uploading...</span>
                           </>
                         ) : (
                           <>
                             <span>Post v{activeNote.snippets.length > 0 ? activeNote.snippets[activeNote.snippets.length - 1].version + 1 : 1}</span>
                             <Send size={12} />
                           </>
                         )}
                     </button>
                 </div>
             </div>
        </div>}

         {/* Bulk Actions Bar - Snippets */}
         {selectedSnippetIds.length > 0 && (
          <div className="absolute bottom-[calc(1.5rem+var(--sab))] right-6 left-6 md:left-auto md:w-64 bg-foreground text-background rounded-lg shadow-xl p-2 flex items-center justify-between z-50 animate-in slide-in-from-bottom-2 fade-in">
              <span className="text-xs font-medium pl-2">{selectedSnippetIds.length} selected</span>
              <button
                onClick={() => bulkDeleteSnippets(
                  selectedSnippetIds,
                  (noteId) => updateNote(noteId, { updated_at: new Date().toISOString() }),
                  () => ui$.selectedSnippetIds.set([])
                )}
                className="p-1.5 hover:bg-white/20 rounded-md transition-colors"
                title="Delete Selected"
              >
                  <Trash2 size={16} />
              </button>
          </div>
      )}

      {/* Lazy-loaded modals */}
      <Suspense fallback={null}>
        {isShareModalOpen && (
          <ShareNoteModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            noteId={activeNoteId}
            noteTitle={activeNote?.title}
          />
        )}

        {isPinSetupModalOpen && (
          <PinSetupModal
            isOpen={isPinSetupModalOpen}
            onClose={() => setIsPinSetupModalOpen(false)}
            onSuccess={handlePinSetupSuccess}
            noteTitle={activeNote?.title}
          />
        )}

        {isPinEntryModalOpen && (
          <PinEntryModal
            isOpen={isPinEntryModalOpen}
            onClose={() => setIsPinEntryModalOpen(false)}
            onSuccess={handleUnlockSuccess}
            onForgotPin={handleForgotPin}
            noteId={activeNoteId}
            noteTitle={activeNote?.title}
          />
        )}

        {isProtectModalOpen && (
          <ProtectNoteModal
            isOpen={isProtectModalOpen}
            onClose={() => setIsProtectModalOpen(false)}
            onConfirm={handleProtectConfirm}
            noteTitle={activeNote?.title}
          />
        )}

        {isPinResetModalOpen && (
          <PinResetModal
            isOpen={isPinResetModalOpen}
            onClose={() => setIsPinResetModalOpen(false)}
            onSuccess={handlePinResetSuccess}
          />
        )}

        {isReportModalOpen && (
          <ReportContentModal
            isOpen={isReportModalOpen}
            onClose={() => setIsReportModalOpen(false)}
            contentType="shared_tag_note"
            contentId={activeNoteId || ''}
          />
        )}
      </Suspense>
    </div>
  );
}
